import Image, { ImageProps } from 'next/image'
import { useMemo } from 'react'
import { MediaEntityType } from '@ancon/wildcat-types'
import getMediaImageClosestSize from '@ancon/wildcat-utils/api/getMediaImageClosestSize'

import getMediaImageCdnUrl from '../utils/getMediaImageCdnUrl'

type OptimizedImageBaseProps = ImageProps & {
  width?: number
  height?: number
  fallbackSize?: string
}

type OptimizedImageProps =
  | (OptimizedImageBaseProps & {
      mediaId: string
      tenantId: string
      mediaEntityType: MediaEntityType
      mediaEntityId: string
    })
  | (OptimizedImageBaseProps & {
      mediaId?: null
      tenantId?: string
      mediaEntityType?: MediaEntityType
      mediaEntityId?: string
    })

export default function OptimizedImage({
  mediaId,
  src,
  width,
  height,
  quality,
  tenantId,
  mediaEntityType,
  mediaEntityId,
  ...rest
}: OptimizedImageProps) {
  const processedSrc = useMemo(() => {
    // Case 1: MediaID - get the image URL with the closest available size
    if (mediaId) {
      const imageSize = getMediaImageClosestSize(width, height)

      return getMediaImageCdnUrl(
        tenantId,
        mediaId,
        mediaEntityType,
        mediaEntityId,
        imageSize,
      )
    }

    // Case 2: StaticImport - pass src as it is
    if (typeof src === 'object') {
      return src
    }

    // Case 3: Local path - pass src as it is
    if (typeof src === 'string' && src.startsWith('/')) {
      return src
    }

    return src
  }, [height, mediaEntityId, mediaEntityType, mediaId, src, tenantId, width])

  return (
    <Image
      src={processedSrc}
      width={width}
      height={height}
      quality={quality}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  )
}
